package fr.frux.shared.dto

import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Portabilité (#172, RGPD art. 20) : **ce que la personne a fourni**, pas ce qu'on a dérivé
 * d'autrui. Les observations de statut, traits et vérifications *des autres* sur ses arbres n'y
 * figurent pas : elles appartiennent à ces autres.
 *
 * [TreeDto] et [HarvestDto] sont réutilisés tels quels plutôt que redéfinis : l'export doit rester
 * lisible par quelqu'un qui connaît l'API, et une forme parallèle divergerait au premier
 * changement de contrat.
 */
@Serializable
data class AccountExportDto(
    @Serializable(with = InstantEpochMillisSerializer::class)
    val exportedAt: Instant,
    val account: UserDto,
    val trees: List<TreeDto>,
    val traits: List<ExportedTraitDto>,
    val verifications: List<ExportedVerificationDto>,
    val favorites: List<ExportedFavoriteDto>,
    val harvestsCreated: List<HarvestDto>,
    val harvestsJoined: List<ExportedParticipationDto>,
    // Récoltes de lieu créées par la personne (#617), séparées de celles d'arbre pour garder le
    // format d'export des consommateurs existants. Ses participations, d'arbre ou de lieu, restent
    // toutes dans [harvestsJoined] : elles ne portent que l'id et la date d'inscription.
    val siteHarvestsCreated: List<HarvestDto> = emptyList(),
    /** Espèces proposées au référentiel (`species.proposed_by`) : l'ADR 0011 les range parmi les
     *  contributions attachées à la personne, au même titre que `trees`/`harvestsCreated`. */
    val speciesProposed: List<ExportedSpeciesProposalDto> = emptyList(),
    /** Photos d'arbres uploadées par la personne (#223, Task 7) : défaut vide, rétro-compatible. */
    val photos: List<ExportedPhotoDto> = emptyList(),
    /** Observations de statut posées par la personne (#208, ADR 0021) : une observation de terrain
     *  est une donnée fournie par l'utilisateur au même titre qu'un trait, elle s'exporte (ADR 0011).
     *  Défaut vide, rétro-compatible. */
    val observations: List<ExportedObservationDto> = emptyList(),
    /** Dons faits par la personne (spec 2026-08-13 cagnotte, RGPD art. 20). Défaut vide,
     *  rétro-compatible. Jamais la référence externe du prestataire ([ExportedDonationDto]) : elle
     *  n'apporte rien à la personne concernée. */
    val donations: List<ExportedDonationDto> = emptyList(),
    /** Suggestions d'espèces déposées par la personne (#233, boîte à suggestions). Défaut vide,
     *  rétro-compatible. */
    val speciesSuggestions: List<ExportedSpeciesSuggestionDto> = emptyList(),
    /** Retours déposés par la personne (#437, ADR 0028). Défaut vide, rétro-compatible. */
    val feedback: List<ExportedFeedbackDto> = emptyList(),
    /** Comptes masqués par la personne (#515, ADR 0029). Défaut vide, rétrocompatible. Jamais les
     *  blocages posés par d'autres sur elle : ils ne sont pas des données qu'elle a fournies. */
    val blockedUsers: List<ExportedBlockedUserDto> = emptyList(),
    /** Signalements de compte posés par la personne (#515). Défaut vide, rétrocompatible. */
    val userReports: List<ExportedUserReportDto> = emptyList(),
    /** Signalements d'arbres, de récoltes et de photos déposés par la personne (#567). Défaut vide,
     *  rétrocompatible. Jamais l'identité d'une autre personne ni celle de qui a traité. */
    val contentReports: List<ExportedContentReportDto> = emptyList(),
    /** Droits Frux+ du compte, révoqués et expirés compris (#344). Défaut vide, rétrocompatible. */
    val entitlements: List<ExportedEntitlementDto> = emptyList(),
    /** Rappels de la vidéo récompensée reçus pour le compte (#531). Défaut vide, rétrocompatible. */
    val rewardedAds: List<ExportedRewardedAdDto> = emptyList(),
    /** Journal des achats Google Play du compte (#457). Défaut vide, rétrocompatible. */
    val playBillingEvents: List<ExportedPlayBillingEventDto> = emptyList(),
    /** Liens de la personne avec des jardins collectifs (ADR 0037). Défaut vide, rétrocompatible. */
    val siteMemberships: List<ExportedSiteMembershipDto> = emptyList(),
)

@Serializable
data class ExportedTraitDto(
    val treeId: Long,
    val kind: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

@Serializable
data class ExportedObservationDto(
    val treeId: Long,
    val status: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val observedAt: Instant,
)

@Serializable
data class ExportedVerificationDto(
    val treeId: Long,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

@Serializable
data class ExportedFavoriteDto(
    val treeId: Long,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

@Serializable
data class ExportedParticipationDto(
    val harvestId: Long,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val joinedAt: Instant,
)

@Serializable
data class ExportedSpeciesProposalDto(
    val speciesId: Long,
    val scientificName: String,
    val commonNameFr: String,
    val status: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

/**
 * Photo d'arbre exportée (#223, RGPD art. 20) : Task 7 du lot photos.
 *
 * [fullUrl] a été ajouté après coup (#245) : l'export de portabilité doit inclure l'original,
 * pas seulement la miniature : sans quoi il ne restituerait qu'une version dégradée de ce que la
 * personne a fourni. Additif volontairement : `photos` de [AccountExportDto] est un DTO d'export
 * JSON lu une fois par la personne concernée, pas un contrat client figé, donc ajouter un champ ne
 * casse rien côté clients existants.
 */
@Serializable
data class ExportedPhotoDto(
    val treeId: Long,
    val photoId: String,
    val thumbUrl: String,
    val fullUrl: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

/**
 * Don exporté (spec 2026-08-13 cagnotte, RGPD art. 20). Volontairement sans référence externe du
 * prestataire (`external_ref`) : c'est un identifiant technique de rejeu webhook, sans valeur pour
 * la personne concernée.
 */
@Serializable
data class ExportedDonationDto(
    val donatedAt: String,
    val amountCents: Long,
    val currency: String,
    val provider: String,
    /** Additifs (#357), défaut à 0/`null` pour rester rétro-compatible. Même format que
     *  [donatedAt] pour [refundedAt]. */
    val refundedCents: Long = 0,
    val refundedAt: String? = null,
)

/**
 * Suggestion d'espèce exportée (#233, RGPD art. 20). `createdAt` en `kotlin.time.Instant`, sérialisé
 * en epoch millis comme les autres timestamps de ce fichier ([ExportedSpeciesProposalDto.createdAt]
 * par ex., cf. #79).
 */
@Serializable
data class ExportedSpeciesSuggestionDto(
    val name: String,
    val comment: String?,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

/**
 * Retour exporté (#437, ADR 0028, RGPD art. 20). Mêmes champs que la ligne admin
 * (`AdminFeedbackRow` côté backend), sans l'id ni l'auteur : la personne concernée les connaît déjà.
 */
@Serializable
data class ExportedFeedbackDto(
    val category: String,
    val message: String,
    val appVersion: String,
    val platform: String,
    val sourceScreen: String,
    val status: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val handledAt: Instant? = null,
)

@Serializable
data class ExportedBlockedUserDto(
    val userId: Long,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val blockedAt: Instant,
)

@Serializable
data class ExportedUserReportDto(
    val reportedUserId: Long,
    val kind: String,
    val details: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
    /** Statut de traitement (#567) : `resolvedAt` nul tant que le signalement est ouvert. */
    @Serializable(with = InstantEpochMillisSerializer::class)
    val resolvedAt: Instant? = null,
    val resolution: String? = null,
)

/**
 * Signalement d'un arbre, d'une récolte ou d'une photo posé par la personne (#567). [targetType]
 * vaut `TREE`, `HARVEST` ou `PHOTO`. La cible peut avoir été supprimée depuis (#568) : son
 * identifiant reste. [kind] est nul pour une photo. [suggestedSpeciesId] est l'espèce que la
 * personne proposait en signalant une mauvaise identification.
 */
@Serializable
data class ExportedContentReportDto(
    val targetType: String,
    val targetId: String,
    val kind: String? = null,
    val suggestedSpeciesId: Long? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val resolvedAt: Instant? = null,
    val resolution: String? = null,
)

/** Un droit Frux+ du compte (#344). Jamais la référence externe du prestataire, comme les dons. */
@Serializable
data class ExportedEntitlementDto(
    val kind: String,
    val source: String,
    val targetRef: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val grantedAt: Instant,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val expiresAt: Instant? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val revokedAt: Instant? = null,
)

/** Un rappel SSV journalisé (#531). Jamais le `transaction_id` de Google, comme la référence d'un don. */
@Serializable
data class ExportedRewardedAdDto(
    val rewardKind: String,
    val outcome: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val receivedAt: Instant,
)

/** Une ligne du journal des achats Play (#457). Jamais le jeton d'achat, ni son hachage. */
@Serializable
data class ExportedPlayBillingEventDto(
    val channel: String,
    val outcome: String,
    val subscriptionState: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val receivedAt: Instant,
)
